import { useEffect, useState } from "react";
import { GiFarmTractor } from "react-icons/gi";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioTipoMaquina } from "../../sync/repositories/fazendas";
import { extrairMensagensErro } from "../../utils/errorUtils";

const AJUDA = {
  titulo: "Tipos de Máquinas",
  descricao:
    "Cadastre as categorias de equipamentos usados na fazenda (Trator, Colheitadeira, Pulverizador, etc.). Esse cadastro é usado na Gestão de Máquinas e nos formulários de Atividades para classificar os equipamentos.",
  secoes: [
    {
      titulo: "Cadastro de Tipo de Máquina",
      campos: [
        { nome: "Nome", obrigatorio: true, explicacao: "Nome do tipo (ex.: Trator, Colheitadeira). Não pode ser duplicado." },
        { nome: "Descrição", obrigatorio: false, explicacao: "Texto livre para detalhar o tipo." },
        { nome: "Status", obrigatorio: true, explicacao: "Tipos inativos não aparecem nos selects de novos cadastros, mas continuam disponíveis para registros antigos." },
      ],
    },
  ],
  dicas: [
    "Diferença entre Tipo e Fabricante: o Tipo é a CATEGORIA do equipamento (o que ele é); o Fabricante é a MARCA (quem fabricou).",
    "Cadastre os Fabricantes e relacione cada um aos Tipos que ele produz — assim o filtro de Fabricantes funciona corretamente na Gestão de Máquinas.",
  ],
};

export default function GestaoTiposMaquinas() {
  const [tipos, setTipos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtros, setFiltros] = useState({ busca: "", status: "todos" });
  const [editando, setEditando] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [formData, setFormData] = useState({ nome: "", descricao: "", ativo: true });
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  const carregar = async () => {
    try {
      setCarregando(true);
      const r = await repositorioTipoMaquina.listar();
      setTipos(r.data.results || r.data);
    } catch (e) {
      console.error(e);
      alert("Erro ao carregar tipos de máquina.");
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const tiposFiltrados = tipos.filter((t) => {
    if (filtros.status === "ativos" && !t.ativo) return false;
    if (filtros.status === "inativos" && t.ativo) return false;
    if (filtros.busca) {
      const b = filtros.busca.toLowerCase();
      return (t.nome || "").toLowerCase().includes(b) || (t.descricao || "").toLowerCase().includes(b);
    }
    return true;
  });

  const handleNovo = () => {
    setEditando(null);
    setFormData({ nome: "", descricao: "", ativo: true });
    setErros([]);
    setMostrarFormulario(true);
  };

  const handleEditar = (t) => {
    setEditando(t);
    setFormData({ nome: t.nome || "", descricao: t.descricao || "", ativo: t.ativo !== false });
    setErros([]);
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setMostrarFormulario(false);
    setEditando(null);
    setFormData({ nome: "", descricao: "", ativo: true });
    setErros([]);
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim()) { setErros(["O nome é obrigatório."]); return; }
    setSalvando(true);
    setErros([]);
    try {
      const payload = {
        nome: formData.nome.trim(),
        descricao: formData.descricao.trim() || null,
        ativo: formData.ativo,
      };
      if (editando) {
        await repositorioTipoMaquina.atualizar(editando.id, payload);
        alert("Tipo de máquina atualizado com sucesso!");
      } else {
        await repositorioTipoMaquina.criar(payload);
        alert("Tipo de máquina cadastrado com sucesso!");
      }
      handleCancel();
      carregar();
    } catch (e2) {
      setErros(extrairMensagensErro(e2));
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (t) => {
    const msg = "Tem certeza que deseja excluir este tipo de máquina? Se houver máquinas vinculadas, ele será apenas inativado.";
    if (!window.confirm(msg)) return;
    try {
      await repositorioTipoMaquina.excluir(t.id);
      alert("Operação concluída.");
      carregar();
    } catch (e) {
      console.error(e);
      alert("Erro ao excluir tipo de máquina.");
    }
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Tipos de Máquinas <GiFarmTractor size={32} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button onClick={handleNovo} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 shadow-md">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Novo Tipo
            </button>
          )}
        </div>

        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-xl font-semibold mb-4">{editando ? "Editar Tipo" : "Cadastrar Novo Tipo"}</h3>
            <form onSubmit={handleSalvar} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  required disabled={salvando}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows={3}
                  className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  disabled={salvando}
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                    disabled={salvando}
                    className="rounded text-green-600 focus:ring-green-500"
                  />
                  Ativo
                </label>
              </div>

              {erros.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
                  {erros.map((m, i) => <div key={i}>{m}</div>)}
                </div>
              )}

              <div className="flex gap-3">
                <button type="submit" disabled={salvando} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded disabled:bg-blue-300">
                  {salvando ? "Salvando..." : (editando ? "Atualizar" : "Cadastrar")}
                </button>
                <button type="button" onClick={handleCancel} disabled={salvando} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded">
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {!mostrarFormulario && (
          <>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Filtros</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
                  <input
                    type="text"
                    value={filtros.busca}
                    onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
                    placeholder="Nome ou descrição..."
                    className="w-full border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={filtros.status}
                    onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                    className="w-full border border-green-500 p-2 rounded"
                  >
                    <option value="todos">Todos</option>
                    <option value="ativos">Apenas ativos</option>
                    <option value="inativos">Apenas inativos</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b bg-gray-50 text-gray-600">
                {tiposFiltrados.length} tipo(s) encontrado(s)
              </div>
              {carregando ? (
                <div className="p-8 text-center text-gray-500">Carregando...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Código</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Nome</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Status</th>
                        <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tiposFiltrados.map((t) => (
                        <tr key={t.id} className="border-t border-gray-200 hover:bg-gray-50">
                          <td className="p-3 text-sm font-mono">{t.id}</td>
                          <td className="p-3 text-sm font-medium">{t.nome}</td>
                          <td className="p-3 text-sm">{t.descricao || "—"}</td>
                          <td className="p-3 text-sm">
                            {t.ativo !== false ? (
                              <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs">Ativo</span>
                            ) : (
                              <span className="px-2 py-1 rounded-full bg-gray-200 text-gray-700 text-xs">Inativo</span>
                            )}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button onClick={() => handleEditar(t)} className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm">Editar</button>
                              <button onClick={() => handleExcluir(t)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm">Excluir</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {tiposFiltrados.length === 0 && (
                        <tr><td colSpan="5" className="p-8 text-center text-gray-500">Nenhum tipo encontrado.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
